// src/pages/student/FeedbackAndFiles.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Chip,
    Grid,
    Slide,
    Card,
    CardContent,
    CardActions,
    Avatar,
    Tabs,
    Tab,
    Badge,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    ListItemSecondaryAction,
    Divider,
    useTheme,
    Tooltip,
    Alert,
    CircularProgress,
    Fade,
    Zoom,
    LinearProgress,
    Menu,
    MenuItem,
    useMediaQuery
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    CheckCircle,
    Cancel,
    Warning,
    Schedule,
    Description,
    Send,
    Download,
    Delete,
    Visibility,
    VisibilityOff,
    Markunread,
    DoneAll,
    Attachment,
    Image,
    PictureAsPdf,
    InsertDriveFile,
    Message,
    Assignment,
    Event,
    ExpandMore,
    MoreVert,
    Check,
    Close,
    Info,
    CloudUpload
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIFeedbackList,
    APIFilesList,
    APIUnreadCount,
    APIMarkFeedbackRead,
    APIAcknowledgeFeedback,
    APIUploadFile,
    APIDeleteFile
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    getUserDetails
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
// NOTE: The component file is still named StudentBar.js
// but it now renders "Researcher Portal" as its visible title.
import StudentBar from '../../components/StudentBar';
import authService from '../../apiServices/authService';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const FeedbackAndFiles = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [feedback, setFeedback] = useState([]);
    const [files, setFiles] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [activeTab, setActiveTab] = useState(0);
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [openFeedbackDialog, setOpenFeedbackDialog] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadDescription, setUploadDescription] = useState('');
    const [uploadMilestoneId, setUploadMilestoneId] = useState('');
    const [uploading, setUploading] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const isMounted = useRef(true);
    const studentId = getUserDetails("Id");

    // Handle logout
    const handleLogout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchAllData();
        
        return () => {
            isMounted.current = false;
        };
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        setError(null);

        try {
            await Promise.all([
                fetchFeedback(),
                fetchFiles(),
                fetchUnreadCount()
            ]);
        } catch (error) {
            setError("Failed to load data");
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchFeedback = async () => {
        const result = await apiService.listById(APIFeedbackList, studentId);
        if (result.success && isMounted.current) {
            setFeedback(Array.isArray(result.data) ? result.data : []);
        }
    };

    const fetchFiles = async () => {
        const result = await apiService.listById(APIFilesList, studentId);
        if (result.success && isMounted.current) {
            setFiles(Array.isArray(result.data) ? result.data : []);
        }
    };

    const fetchUnreadCount = async () => {
        const result = await apiService.listById(APIUnreadCount, studentId);
        if (result.success && result.data && isMounted.current) {
            setUnreadCount(result.data.unread_count || 0);
        }
    };

    const handleMarkAsRead = async (feedbackItem) => {
        try {
            const formData = new FormData();
            formData.append('feedback_id', feedbackItem.id);
            
            const result = await apiService.update(APIMarkFeedbackRead, formData);
            
            if (result && result.success) {
                showToast("Success", "Feedback marked as read", "success");
                fetchFeedback();
                fetchUnreadCount();
            }
        } catch (error) {
            showToast("Error", "Failed to mark as read", "error");
        }
    };

    const handleAcknowledge = async (feedbackItem) => {
        try {
            const formData = new FormData();
            formData.append('feedback_id', feedbackItem.id);
            
            const result = await apiService.update(APIAcknowledgeFeedback, formData);
            
            if (result && result.success) {
                showToast("Success", "Feedback acknowledged", "success");
                fetchFeedback();
            }
        } catch (error) {
            showToast("Error", "Failed to acknowledge", "error");
        }
    };

    const handleViewFeedback = (item) => {
        setSelectedFeedback(item);
        setOpenFeedbackDialog(true);
        if (!item.is_read) {
            handleMarkAsRead(item);
        }
    };

    const handleFileUpload = async () => {
        if (!uploadFile) {
            showToast("Error", "Please select a file to upload", "error");
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('student_id', studentId);
            formData.append('file', uploadFile);
            if (uploadDescription) {
                formData.append('description', uploadDescription);
            }
            if (uploadMilestoneId) {
                formData.append('milestone_id', uploadMilestoneId);
            }
            
            const result = await apiService.create(APIUploadFile, formData);
            
            if (result && result.success) {
                showToast("Success", "File uploaded successfully", "success");
                setUploadFile(null);
                setUploadDescription('');
                setUploadMilestoneId('');
                fetchFiles();
                document.getElementById('file-input').value = '';
            }
        } catch (error) {
            showToast("Error", "Failed to upload file", "error");
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteFile = async (file) => {
        try {
            const formData = new FormData();
            formData.append('file_id', file.id);
            
            const result = await apiService.delete(APIDeleteFile, formData);
            
            if (result && result.success) {
                showToast("Success", "File deleted successfully", "success");
                fetchFiles();
            }
        } catch (error) {
            showToast("Error", "Failed to delete file", "error");
        }
        handleMenuClose();
    };

    const handleDownloadFile = (file) => {
        window.open(file.file_url, '_blank');
        handleMenuClose();
    };

    const handleMenuOpen = (event, file) => {
        setSelectedFile(file);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedFile(null);
    };

    const getFeedbackIcon = (type) => {
        switch(type) {
            case 'general': return <Message />;
            case 'milestone_review': return <Assignment />;
            case 'document_review': return <Description />;
            case 'meeting_notes': return <Event />;
            case 'urgent': return <Warning />;
            default: return <Message />;
        }
    };

    const getFileIcon = (fileType) => {
        if (fileType?.includes('pdf')) return <PictureAsPdf color="error" />;
        if (fileType?.includes('image')) return <Image color="primary" />;
        return <InsertDriveFile color="action" />;
    };

    if (loading) {
        return <LoadingState message="Loading Feedback & Files..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchAllData} title="Unable to Load Data" variant="research" fullPage retryText="Retry Loading" />;
    }

    return (
        <>
            
            <FullWidthContainer >
                <Box sx={{backgroundColor: 'white',borderRadius: 3 }}>
                    <StudentBar notificationCount={unreadCount} />

                    {/* Header Section */}
                    <Paper elevation={2} sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        mb: { xs: 2, sm: 3 }, 
                        // borderRadius: 3,
                        // borderBottomLeftRadius:10,
                        backgroundColor: 'white',
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between', 
                            flexWrap: 'wrap', 
                            gap: 2,
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Typography variant="h6" sx={{
                                fontWeight: 'bold',
                                color: theme.palette.primary.main,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                                <Message /> Feedback & Files
                            </Typography>

                            <Button
                                variant="outlined"
                                color="info"
                                startIcon={<RefreshIcon />}
                                onClick={fetchAllData}
                                sx={{ 
                                    textTransform: 'none', 
                                    borderRadius: '8px',
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Paper>
                </Box>
                {/* Tabs */}
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'hidden',
                    backgroundColor: 'white',
                }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider', 
                            px: { xs: 1, sm: 2 }, 
                            pt: 2,
                            '& .MuiTab-root': {
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                minWidth: { xs: 'auto', sm: 120 },
                                px: { xs: 1, sm: 2 }
                            }
                        }}
                        variant={isMobile ? "scrollable" : "standard"}
                        scrollButtons={isMobile ? "auto" : false}
                    >
                        <Tab 
                            label={
                                <Badge badgeContent={unreadCount} color="error" sx={{ mr: 1 }}>
                                    Feedback
                                </Badge>
                            } 
                        />
                        <Tab label="Files" />
                    </Tabs>

                    {/* Feedback Tab */}
                    {activeTab === 0 && (
                        <Box sx={{ p: { xs: 2, sm: 3 } }}>
                            {feedback.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 } }}>
                                    <Message sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
                                    <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        No feedback received yet
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Your supervisor's feedback will appear here
                                    </Typography>
                                </Box>
                            ) : (
                                <Grid container spacing={2}>
                                    {feedback.map((item, index) => (
                                        <Grid item xs={12} key={item.id}>
                                            <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }}>
                                                <Card sx={{ 
                                                    cursor: 'pointer',
                                                    transition: 'transform 0.2s',
                                                    '&:hover': { transform: 'translateX(4px)' },
                                                    borderLeft: `4px solid ${theme.palette[item.type_color]?.main || theme.palette.primary.main}`,
                                                    opacity: item.is_read ? 0.8 : 1
                                                }}>
                                                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                                        <Box sx={{ 
                                                            display: 'flex', 
                                                            justifyContent: 'space-between', 
                                                            alignItems: 'flex-start', 
                                                            flexWrap: 'wrap', 
                                                            gap: 1 
                                                        }}>
                                                            <Box sx={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                gap: { xs: 1, sm: 2 },
                                                                flexWrap: 'wrap'
                                                            }}>
                                                                <Avatar sx={{ 
                                                                    bgcolor: `${theme.palette[item.type_color]?.main || theme.palette.primary.main}20`, 
                                                                    color: theme.palette[item.type_color]?.main || theme.palette.primary.main,
                                                                    width: { xs: 40, sm: 48 },
                                                                    height: { xs: 40, sm: 48 }
                                                                }}>
                                                                    {getFeedbackIcon(item.feedback_type)}
                                                                </Avatar>
                                                                <Box>
                                                                    <Typography variant="subtitle1" sx={{ 
                                                                        fontWeight: 'bold',
                                                                        fontSize: { xs: '0.875rem', sm: '1rem' }
                                                                    }}>
                                                                        {item.type_display_name}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                                        From: {item.supervisor_name} • {item.time_ago}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                                {!item.is_read && (
                                                                    <Chip size="small" label="New" color="error" sx={{ height: { xs: 20, sm: 24 } }} />
                                                                )}
                                                                {item.requires_action && (
                                                                    <Chip size="small" label="Action Required" color="warning" sx={{ height: { xs: 20, sm: 24 } }} />
                                                                )}
                                                                {item.feedback_type === 'urgent' && (
                                                                    <Chip size="small" label="Urgent" color="error" sx={{ height: { xs: 20, sm: 24 } }} />
                                                                )}
                                                            </Box>
                                                        </Box>
                                                        
                                                        <Typography variant="body2" sx={{ 
                                                            mt: 2, 
                                                            ml: { xs: 0, sm: 7 },
                                                            color: 'text.secondary',
                                                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                                        }}>
                                                            {item.feedback_content.length > (isMobile ? 100 : 200)
                                                                ? item.feedback_content.substring(0, isMobile ? 100 : 200) + '...' 
                                                                : item.feedback_content}
                                                        </Typography>
                                                        
                                                        <Box sx={{ 
                                                            display: 'flex', 
                                                            gap: 1, 
                                                            mt: 2, 
                                                            ml: { xs: 0, sm: 7 },
                                                            flexDirection: { xs: 'column', sm: 'row' }
                                                        }}>
                                                            <Button 
                                                                size="small" 
                                                                variant="outlined" 
                                                                onClick={() => handleViewFeedback(item)}
                                                                sx={{ width: { xs: '100%', sm: 'auto' } }}
                                                            >
                                                                View Details
                                                            </Button>
                                                            {!item.is_read && (
                                                                <Button 
                                                                    size="small" 
                                                                    startIcon={<Markunread />}
                                                                    onClick={() => handleMarkAsRead(item)}
                                                                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                                                                >
                                                                    Mark as Read
                                                                </Button>
                                                            )}
                                                            {item.requires_action && item.feedback_status === 'read' && (
                                                                <Button 
                                                                    size="small" 
                                                                    color="success"
                                                                    startIcon={<DoneAll />}
                                                                    onClick={() => handleAcknowledge(item)}
                                                                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                                                                >
                                                                    Acknowledge
                                                                </Button>
                                                            )}
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Zoom>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                    )}

                    {/* Files Tab */}
                    {activeTab === 1 && (
                        <Box sx={{ p: { xs: 2, sm: 3 } }}>
                            {/* Upload Section */}
                            <Paper sx={{ 
                                p: { xs: 2, sm: 3 }, 
                                mb: 3, 
                                bgcolor: theme.palette.grey[50],
                                borderRadius: 2
                            }}>
                                <Typography variant="subtitle1" sx={{ 
                                    fontWeight: 'bold', 
                                    mb: 2, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1,
                                    fontSize: { xs: '0.875rem', sm: '1rem' }
                                }}>
                                    <CloudUpload /> Upload New File
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            component="label"
                                            startIcon={<Attachment />}
                                            sx={{ py: { xs: 1.5, sm: 2 } }}
                                        >
                                            Choose File
                                            <input
                                                id="file-input"
                                                type="file"
                                                hidden
                                                onChange={(e) => setUploadFile(e.target.files[0])}
                                            />
                                        </Button>
                                        {uploadFile && (
                                            <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                Selected: {uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(2)} MB)
                                            </Typography>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Description (Optional)"
                                            value={uploadDescription}
                                            onChange={(e) => setUploadDescription(e.target.value)}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            variant="contained"
                                            onClick={handleFileUpload}
                                            disabled={!uploadFile || uploading}
                                            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
                                            fullWidth={isMobile}
                                            sx={{ width: { xs: '100%', sm: 'auto' } }}
                                        >
                                            {uploading ? 'Uploading...' : 'Upload File'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Files List */}
                            {files.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 } }}>
                                    <InsertDriveFile sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
                                    <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        No files uploaded yet
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Upload your research documents here
                                    </Typography>
                                </Box>
                            ) : (
                                <List sx={{ px: { xs: 0, sm: 1 } }}>
                                    {files.map((file) => (
                                        <React.Fragment key={file.id}>
                                            <ListItem
                                                sx={{
                                                    '&:hover': { bgcolor: theme.palette.action.hover },
                                                    borderRadius: 2,
                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                    alignItems: { xs: 'flex-start', sm: 'center' },
                                                    py: { xs: 2, sm: 1 }
                                                }}
                                            >
                                                <ListItemAvatar>
                                                    <Avatar sx={{ bgcolor: theme.palette.info.light + '20' }}>
                                                        {getFileIcon(file.file_type)}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            gap: 1, 
                                                            flexWrap: 'wrap',
                                                            mb: { xs: 1, sm: 0 }
                                                        }}>
                                                            <Typography variant="subtitle2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                                                {file.file_name}
                                                            </Typography>
                                                            <Chip 
                                                                label={file.file_size} 
                                                                size="small" 
                                                                variant="outlined" 
                                                                sx={{ height: { xs: 20, sm: 24 } }}
                                                            />
                                                            <Chip 
                                                                label={file.file_status?.replace(/_/g, ' ').toUpperCase()} 
                                                                size="small" 
                                                                color={file.status_color}
                                                                sx={{ height: { xs: 20, sm: 24 } }}
                                                            />
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                                Uploaded: {formatCreatedAtDate(file.uploaded_date)} • Version {file.version_number}
                                                            </Typography>
                                                            {file.description && (
                                                                <Typography variant="body2" sx={{ mt: 0.5, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                                                                    {file.description}
                                                                </Typography>
                                                            )}
                                                            {file.revision_feedback && (
                                                                <Alert severity="warning" sx={{ mt: 1, p: { xs: 0.5, sm: 1 } }}>
                                                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                                        {file.revision_feedback}
                                                                    </Typography>
                                                                </Alert>
                                                            )}
                                                        </>
                                                    }
                                                    sx={{ 
                                                        flex: 1,
                                                        mr: { xs: 0, sm: 2 },
                                                        mb: { xs: 1, sm: 0 }
                                                    }}
                                                />
                                                <ListItemSecondaryAction sx={{ 
                                                    position: { xs: 'relative', sm: 'absolute' },
                                                    right: { sm: 16 },
                                                    top: { sm: '50%' },
                                                    transform: { sm: 'translateY(-50%)' }
                                                }}>
                                                    <IconButton edge="end" onClick={(e) => handleMenuOpen(e, file)}>
                                                        <MoreVert />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                            <Divider />
                                        </React.Fragment>
                                    ))}
                                </List>
                            )}
                        </Box>
                    )}
                </Paper>

                {/* Feedback Detail Dialog */}
                <Dialog
                    open={openFeedbackDialog}
                    onClose={() => setOpenFeedbackDialog(false)}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="md"
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ textAlign: 'center', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        <b>Feedback Details</b>
                    </DialogTitle>
                    <DialogContent>
                        {selectedFeedback && (
                            <Box sx={{ p: { xs: 1, sm: 2 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                                    <Avatar sx={{ bgcolor: theme.palette[selectedFeedback.type_color]?.main }}>
                                        {getFeedbackIcon(selectedFeedback.feedback_type)}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                            {selectedFeedback.type_display_name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            From: {selectedFeedback.supervisor_name} • {selectedFeedback.time_ago}
                                        </Typography>
                                    </Box>
                                </Box>
                                
                                <Divider sx={{ my: 2 }} />
                                
                                <Typography variant="body1" sx={{ 
                                    whiteSpace: 'pre-wrap', 
                                    mb: 3,
                                    fontSize: { xs: '0.875rem', sm: '1rem' }
                                }}>
                                    {selectedFeedback.feedback_content}
                                </Typography>
                                
                                {selectedFeedback.inline_comments && selectedFeedback.inline_comments.length > 0 && (
                                    <>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            Inline Comments:
                                        </Typography>
                                        {selectedFeedback.inline_comments.map((comment, idx) => (
                                            <Paper key={idx} sx={{ p: 2, mb: 1, bgcolor: theme.palette.warning.light + '10' }}>
                                                <Typography variant="caption" color="primary">{comment.location}</Typography>
                                                <Typography variant="body2">{comment.comment}</Typography>
                                            </Paper>
                                        ))}
                                    </>
                                )}
                                
                                {selectedFeedback.attachments && selectedFeedback.attachments.length > 0 && (
                                    <>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
                                            Attachments:
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {selectedFeedback.attachments.map((attachment, idx) => (
                                                <Chip key={idx} label={attachment} icon={<Attachment />} size="small" />
                                            ))}
                                        </Box>
                                    </>
                                )}
                                
                                {selectedFeedback.requires_action && (
                                    <Alert severity="warning" sx={{ mt: 2 }}>
                                        <Typography variant="body2">
                                            Action Required by: {selectedFeedback.action_deadline ? new Date(selectedFeedback.action_deadline).toLocaleDateString() : 'ASAP'}
                                        </Typography>
                                    </Alert>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenFeedbackDialog(false)} variant="contained">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* File Actions Menu */}
                <Menu 
                    anchorEl={anchorEl} 
                    open={Boolean(anchorEl)} 
                    onClose={handleMenuClose}
                    PaperProps={{
                        sx: {
                            borderRadius: 2,
                            minWidth: 150
                        }
                    }}
                >
                    <MenuItem onClick={() => handleDownloadFile(selectedFile)}>
                        <Download sx={{ mr: 1 }} /> Download
                    </MenuItem>
                    <MenuItem onClick={() => handleDeleteFile(selectedFile)} sx={{ color: 'error.main' }}>
                        <Delete sx={{ mr: 1 }} /> Delete
                    </MenuItem>
                </Menu>
            </FullWidthContainer>
        </>
    );
};

export default FeedbackAndFiles;